import { useBlockCompletion, useBlocksConfig } from "@pathwright/blocks-engine"
import Button from "@pathwright/ui/src/components/button/Button"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import moment from "moment"
import PropTypes from "prop-types"
import { Fragment, useCallback, useMemo } from "react"
import styled from "styled-components"
import { SCORING_GREEN, SCORING_RED } from "../blocks/Question/ui"

const Meta = styled.div`
  display: flex;
  align-items: center;
  font-style: italic;
  color: #666;
  font-size: 0.95em;
`

const StyledSubmitButton = styled(SubmitButton)`
  opacity: 0;
  transition: all 0.4s linear;
  ${(p) =>
    p.show
      ? `
      opacity: 1;
    `
      : ``}
`

const BlockSubmit = ({
  answeredDate,
  buttonLabel,
  onSubmit: onSubmitToServer,
  onUnsubmit: onUnsubmitToServer,
  isSubmitted
}) => {
  const { t, tc } = useTranslate()
  const {
    showSubmit,
    useAutocomplete,
    isSubmitting,
    onSubmit: onSubmitOptimistic,
    onUnsubmit: onUnsubmitOptimistic,
    score,
    isCompleted
  } = useBlockCompletion()

  const { hideAssessmentFeedback, disableInput } = useBlocksConfig()

  const handleSubmit = useCallback(() => {
    onSubmitOptimistic()
    onSubmitToServer()
  }, [isSubmitting, onSubmitOptimistic, onSubmitToServer])

  const handleUnsubmit = useCallback(() => {
    onUnsubmitOptimistic()
    onUnsubmitToServer()
  }, [isSubmitting, onUnsubmitOptimistic, onUnsubmitToServer])

  const resultAdverb = useMemo(
    () =>
      !hideAssessmentFeedback && score === 0
        ? tc("blocks.question.incorrectly")
        : !hideAssessmentFeedback && score === 1
        ? tc("blocks.question.correctly")
        : null,
    [score, hideAssessmentFeedback, tc]
  )

  const answered = useMemo(
    () =>
      resultAdverb !== null && !hideAssessmentFeedback
        ? ` ${t("blocks.question.answered").toLowerCase()}`
        : tc("blocks.question.answered"),
    [resultAdverb, hideAssessmentFeedback, t, tc]
  )

  if (useAutocomplete) return null

  if (!isSubmitted) {
    return (
      <StyledSubmitButton
        brand
        onClick={handleSubmit}
        submitting={isSubmitting}
        disabled={disableInput}
        styleType="primary"
        style={{ marginTop: "1rem" }}
        show={showSubmit}
        label={buttonLabel || t("blocks.question.submit_answer")}
        preIcon="check"
      />
    )
  }

  if (isSubmitting && !isCompleted) return <LoadingCircle center={false} />

  return (
    <Meta>
      {score !== null && !hideAssessmentFeedback && (
        <Pathicon
          icon={score === 0 ? "x" : "check"}
          style={{
            marginRight: "4px",
            color: score === 0 ? SCORING_RED : SCORING_GREEN
          }}
        />
      )}
      {resultAdverb}
      {answered} {answeredDate ? moment(answeredDate).fromNow() : ""}
      {disableInput ? null : (
        <Fragment>
          {" • "}
          <Button
            style={{ marginLeft: "4px" }}
            brand
            styleType="text"
            onClick={handleUnsubmit}
          >
            {t("blocks.question.reset_answer")}
          </Button>
        </Fragment>
      )}
    </Meta>
  )
}

BlockSubmit.propTypes = {
  answeredDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  buttonLabel: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onUnsubmit: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool
}

export default BlockSubmit
